<template>
    <div class="page-wrap">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Foundations</div>
                <div class="page-bar-main">
                    <span v-if="selectedType == 'ALL'">Make sure to view and sign off on updated items</span>
                    <span v-else-if="selectedType == 'NODE'">Folders</span>
                    <span v-else-if="selectedType == 'LINK'">Links</span>
                    <span v-else-if="selectedType == 'VIDEO'">Videos</span>
                    <span v-else-if="selectedType == 'PROCESS'">Processes</span>
                    <span v-else-if="selectedType == 'TIMELINE'">Value Engines</span>
                    <span v-else-if="selectedType == 'SHEET'">Role Sheets</span>
                    <span v-else-if="selectedType == 'CHECKLIST'">Powerlists</span>
                </div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div class="page-filters pf-bar" v-if="selectedType != 'ALL'">
                <div v-if="selectedType == 'PROCESS' || selectedType == 'VIDEO' || selectedType == 'LINK'" class="pf-seg">
                    <button type="button" :class="{ on: selectedType == 'PROCESS' }" @click="$router.push('/nodes/PROCESS')">Processes</button>
                    <button type="button" :class="{ on: selectedType == 'VIDEO' }" @click="$router.push('/nodes/VIDEO')">Videos</button>
                    <button type="button" :class="{ on: selectedType == 'LINK' }" @click="$router.push('/nodes/LINK')">Links</button>
                </div>
                <div class="pf-search">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" /></svg>
                    <input type="text" v-model="searchQ" @input="search()" placeholder="Search" aria-label="Search" />
                </div>
                <select v-if="$store.state.user.isAdmin" v-model="selectedLocation" @change="search()">
                    <option value="ALL">All Locations</option>
                    <option v-for="(loc, i) in $store.state.user.locations" :key="i" :value="loc">{{ loc }}</option>
                </select>
                <select v-model="selectedDepartment" @change="search()">
                    <option value="ALL">All Departments</option>
                    <option v-for="(dep, i) in $store.state.user.departments" :key="i" :value="dep">{{ dep }}</option>
                </select>
                <select v-model="selectedNodeType" @change="search()">
                    <option value="ALL">All Types</option>
                    <option v-for="(type, i) in $store.state.user.nodeTypes" :key="i" :value="type">{{ type }}</option>
                </select>
                <span v-if="selectedType == 'PROCESS' || $store.state.adminRolesCanEdit.includes($store.state.user.role)" class="pf-spacer"></span>
                <!-- A general suggestion, not for one item (those use Suggest an Improvement on the item's page) -->
                <button v-if="selectedType == 'PROCESS'" type="button" class="btn btn-light" @click="showSuggestion = true">Make a Suggestion</button>
                <template v-if="$store.state.adminRolesCanEdit.includes($store.state.user.role)">
                    <div class="ai-btn" @click="showAICreate = true">
                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg>
                        The Forge
                    </div>
                    <div @click="() => { showAddProcess = true }" class="add-btn" title="Add manually">Add</div>
                </template>
            </div>
            <div>
                <table class="full-table">
                    <tbody>
                        <tr class="table-labels">
                            <td class="table-sort-label" @click="changeSort('Name')">
                                <span>Name</span>
                                <span v-if="sortBy == 'Name' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'Name' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            <td v-if="showSignOff">Signed</td>
                            <td v-if="selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET'" class="table-sort-label" @click="changeSort('ProcessOwnerNames')">
                                <span>Process Owner</span>
                                <span v-if="sortBy == 'ProcessOwnerNames' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'ProcessOwnerNames' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            <td v-if="$store.state.user.isAdmin || showSignOff" class="table-sort-label" @click="changeSort('LastModified')">
                                <span>Last Modified</span>
                                <span v-if="sortBy == 'LastModified' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'LastModified' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            <td v-if="$store.state.user.isAdmin || showSignOff" class="table-sort-label" @click="changeSort('LastUpdatedBy')">
                                <span>Last Modifed By</span>
                                <span v-if="sortBy == 'LastUpdatedBy' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'LastUpdatedBy' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            <!--
                            <td v-if="$store.state.user.isAdmin && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')" class="table-sort-label" @click="changeSort('LastApproved')">
                                <span>Last Approved</span>
                                <span v-if="sortBy == 'LastApproved' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'LastApproved' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            -->
                            <!--
                            <td v-if="$store.state.user.isAdmin && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')" class="table-sort-label" @click="changeSort('LastApprovedBy')">
                                <span>Last Approved By</span>
                                <span v-if="sortBy == 'LastApprovedBy' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'LastApprovedBy' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            -->
                            <!--
                            <td v-if="!showSignOff && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')" class="table-sort-label" @click="changeSort('LastReviewed')">
                                <span>Last Reviewed</span>
                                <span v-if="sortBy == 'LastReviewed' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'LastReviewed' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            <td v-if="$store.state.user.isAdmin && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')" class="table-sort-label" @click="changeSort('LastReviewedBy')">
                                <span>Last Reviewed By</span>
                                <span v-if="sortBy == 'LastReviewedBy' && sortDirection == 'ASC'"><svg-icon icon="smtriangleup" /></span>
                                <span v-if="sortBy == 'LastReviewedBy' && sortDirection == 'DESC'"><svg-icon icon="smtriangledown" /></span>
                            </td>
                            -->
                            <td style="padding:0"></td>
                        </tr>
                        <tr style="padding:0;height:0"></tr>
                        <template v-for="(process, i) in processes" :key="i">
                            <tr>
                                <td v-if="process.Type == 'LINK'">
                                    <button type="button" :class="isFavorite(process) ? 'fav-star on' : 'fav-star'" :title="isFavorite(process) ? 'Remove from favorites' : 'Add to favorites'" @click="toggleFavorite(process)"><svg-icon :icon="isFavorite(process) ? 'starfilled' : 'star'" /></button>
                                    <a :href="process.Link" target="_blank">{{ process.Name }}</a>
                                </td>
                                <td v-else>
                                    <button type="button" :class="isFavorite(process) ? 'fav-star on' : 'fav-star'" :title="isFavorite(process) ? 'Remove from favorites' : 'Add to favorites'" @click="toggleFavorite(process)"><svg-icon :icon="isFavorite(process) ? 'starfilled' : 'star'" /></button>
                                    <router-link :to="'/playbook/' + process.Slug"><ai-forge-name :name="process.Name" /></router-link>
                                </td>
                                <td v-if="showSignOff" style="white-space:nowrap"><svg-icon v-if="process.HasSignOff" icon="check" style="width:22px;height:22px;margin-bottom:-5px" /><span v-if="process.SignOffDate">&nbsp;{{ process.SignOffDate }}</span></td>
                                <td v-if="selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET'">{{ process.ProcessOwnerNames }}</td>
                                <td v-if="$store.state.user.isAdmin || showSignOff">{{ process.LastModifiedString }}</td>
                                <td v-if="$store.state.user.isAdmin || showSignOff">{{ process.LastUpdatedBy }}</td>
                                <!--
                                <td v-if="$store.state.user.isAdmin && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')">{{ process.LastApprovedString }}</td>
                                -->
                                <!--
                                <td v-if="$store.state.user.isAdmin && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')">{{ process.LastApprovedBy }}</td>
                                -->
                                <!--
                                <td v-if="!showSignOff && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')">{{ process.LastReviewedString }}</td>
                                <td v-if="$store.state.user.isAdmin && (selectedType == 'ALL' || selectedType == 'PROCESS' || selectedType == 'TIMELINE' || selectedType == 'SHEET')">{{ process.LastReviewedBy }}</td>
                                -->
                                <td v-if="rowMenuAvailable(process)" style="text-align:center">
                                    <div class="row-menu">
                                        <button type="button" class="row-menu-btn" @click.stop="toggleRowMenu(process)">
                                            <svg-icon icon="dots" />
                                        </button>
                                        <div v-if="openMenuSlug == process.Slug" class="row-menu-dropdown">
                                            <a v-if="canEditNodes" @click.prevent="rowMenuAction(() => getRoutes(process))">Routes</a>
                                            <a v-if="showsShare(process)" @click.prevent="rowMenuAction(() => sharePDF(process))">Share</a>
                                            <a v-if="showsPDF(process)" @click.prevent="rowMenuAction(() => getPDF(process))">PDF</a>
                                            <a v-if="canEditNodes" @click.prevent="rowMenuAction(() => editProcess(process, false))">Edit Details</a>
                                            <router-link v-if="canEditNodes && process.Type == 'PROCESS'" :to="'/edit-process/' + process.Slug" @click="closeRowMenu">Edit Process</router-link>
                                            <router-link v-if="canEditNodes && process.Type == 'TIMELINE'" :to="'/edit-timeline/' + process.Slug" @click="closeRowMenu">Edit Value Engine</router-link>
                                            <router-link v-if="canEditNodes && process.Type == 'SHEET'" :to="'/edit-sheet/' + process.Slug" @click="closeRowMenu">Edit Sheet</router-link>
                                            <router-link v-if="canEditNodes && process.Type == 'CHECKLIST'" :to="'/edit-checklist/' + process.Slug" @click="closeRowMenu">Edit Checklist</router-link>
                                            <a v-if="canEditNodes" @click.prevent="rowMenuAction(() => editProcess(process, true))">Duplicate</a>
                                            <a v-if="canEditNodes" @click.prevent="rowMenuAction(() => addToFolder(process))">Add to Folder</a>
                                            <div v-if="canEditNodes" class="row-menu-divider"></div>
                                            <a v-if="canEditNodes" class="row-menu-delete" @click.prevent="rowMenuAction(() => deleteProcess(process))">Remove</a>
                                        </div>
                                    </div>
                                </td>
                                <td v-else style="padding:0"></td>
                            </tr>
                            <tr v-if="process.showRoutes">
                                <td colspan="42">
                                    <span v-if="!process.routes.length">No Routes</span>
                                    <div v-for="(route, j) in process.routes" :key="'r'+j">
                                        <span v-for="(r, k) in route" :key="'rr'+k"><router-link :to="'/playbook/'+r">{{ r }}</router-link>&nbsp;/&nbsp;</span>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="showConfirmDeleteOrDraft" class="error-msg-wrap">
            <div class="error-msg">
                <div>Would you like to remove this item?</div>
                <div style="margin-bottom:10px"><div @click="unapproveItem('Draft')" class="btn">Yes, Move to Draft</div></div>
                <div style="margin-bottom:10px"><div @click="unapproveItem('Needs Approval')" class="btn">Yes, Move to Needs Approval</div></div>
                <div style="margin-bottom:10px"><div @click="unapproveItem('Inactive')" class="btn">Yes, Move to Inactive</div></div>
                <div><div @click="() => { showConfirmDeleteOrDraft = false; }" class="btn-invert">Cancel</div></div>
            </div>
        </div>

        <div v-if="showSharePDF" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showSharePDF = false }">&times;</button>
                </div>
                <div style="margin-bottom:15px;text-decoration:underline"><a @click.prevent="copyLink()">Copy Link</a></div>
                <h3>Email</h3>
                <input type="text" v-model="shareData.email" placeholder="Email" />
                <h3>Phone</h3>
                <phone-input v-model="shareData.phone" placeholder="Phone" />
                <div class="checkbox-input" v-if="shareProcess.Type == 'CHECKLIST'">
                    <input type="checkbox" v-model="shareData.wDescriptions" />
                    <h3>w/ Lines</h3>
                </div>
                <input :class="gettingPDF ? 'disable-button' : ''" type="submit" value="Send" @click="getPDF(shareProcess, true)" />
            </div>
        </div>

        <pdf-pop-up v-if="showPDF" :url="PDFUrl" @close="() => showPDF = false" />
        <ai-create-node v-if="showAICreate" :node-type="aiCreateType" @close="closeAICreate()" />
        <add-edit-node v-if="showAddProcess" :type="selectedType" @close="closeAddProcess()" />
        <add-edit-node v-if="showEditNode" :node="nodeToEdit" :cloneNode="cloneNode" @close="closeAddProcess()" />
        <confirm-delete v-if="showConfirmDelete" @confirm="deleteProcessConfirm" @cancel="() => { showConfirmDelete = false; }" msg="Are you sure you would like to PERMANENTLY DELETE this item?"/>
        <process-suggestion-form v-if="showSuggestion" @close="showSuggestion = false" />
        <add-node-to-folder v-if="showConfirmAddToFolder" :toSlug="slugToAddToFolder" @close="() => { showConfirmAddToFolder = false; }" />
    </div>
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import PhoneInput from '@/components/PhoneInput.vue';
import { normalizePhone, PHONE_ERROR } from '@/phone';
import AddEditNode from '@/components/AddEditNode.vue';
import AICreateNode from '@/components/AICreateNode.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import PdfPopUp from '@/components/PDFPopUp.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import AddNodeToFolder from '@/components/AddNodeToFolder.vue';
import AIForgeName from '@/components/AIForgeName.vue';
import ProcessSuggestionForm from '@/components/ProcessSuggestionForm.vue';

export default defineComponent({
    name: 'NodesView',
    components: {
        AiForgeName: AIForgeName,
        PhoneInput,
        AddEditNode,
        AiCreateNode: AICreateNode,
        ConfirmDelete,
        PdfPopUp,
        SvgIcon,
        AddNodeToFolder,
        ProcessSuggestionForm
    },
    data() {
        return {
            allProcesses: [] as any,
            processes: [] as any,
            searchQ: '',
            showAddProcess: false,
            showSuggestion: false,
            showAICreate: false,
            showConfirmDeleteOrDraft: false,
            showConfirmDelete: false,
            processToDelete: null as any,
            gettingPDF: false,
            showPDF: false,
            PDFUrl: '',
            showEditNode: false,
            nodeToEdit: null as any,
            selectedType: 'ALL',
            selectedDepartment: 'ALL',
            selectedNodeType: 'ALL',
            selectedLocation: 'ALL',
            sortBy: 'Name',
            sortDirection: 'ASC',
            cloneNode: false,
            showSignOff: false,
            openMenuSlug: null as any,
            slugToAddToFolder: null as any,
            showConfirmAddToFolder: false,
            shareProcess: null as any,
            shareData: {
                email: '',
                phone: '',
                wDescriptions: false
            },
            showSharePDF: false
        }
    },
    computed: {
        canEditNodes(): boolean {
            return this.$store.state.adminRolesCanEdit.includes(this.$store.state.user.role);
        },
        // "Generate with AI" starts on the type this list is filtered to (process otherwise)
        aiCreateType(): string {
            const map: any = { PROCESS: 'process', CHECKLIST: 'checklist', SHEET: 'sheet', TIMELINE: 'timeline' };
            return map[this.selectedType] || 'process';
        }
    },
    async created() {
        /*
        if(!this.$store.state.user.isAdmin) {
            this.showDateCols = true;
        }
        */

        if(this.$route.params.type) {
            this.selectedType = this.$route.params.type;
        }

        // Pulls and shows sign off column
        if(this.selectedType == 'ALL' && !this.$store.state.user.isAdmin) {
            this.showSignOff = true;
        }

        if(this.selectedType == 'ALL') {
            this.sortBy = 'LastModified';
            this.sortDirection = 'DESC'
        }

        if(this.$route.query.nodeType) {
            this.selectedNodeType = this.$route.query.nodeType;
        }
        if(this.$route.query.location) {
            this.selectedLocation = this.$route.query.location;
        }
        if(this.$route.query.department) {
            this.selectedDepartment = this.$route.query.department;
        }
        if(this.$route.query.search) {
            this.searchQ = this.$route.query.search.toLowerCase();
        }

        await this.getProcesses();

        this.$store.state.routeLoaded = true;
    },
    methods: {
        // Nodes are saved to the menu favorites as 'node:<slug>' (resolved in App.vue favoriteItems)
        isFavorite(node: any): boolean {
            return this.$store.state.navFavorites.includes('node:' + node.Slug);
        },
        toggleFavorite(node: any) {
            this.$store.dispatch('toggleFavorite', 'node:' + node.Slug);
        },
        async getProcesses() {
            await axios.get(`${this.$store.state.apiUrl}/api/playbook/nodes`)
                .then(resp => {
                    const userID = this.$store.state.user.userID;
                    resp.data.forEach((p: any) => {
                        if(p.LastModified) {
                            p.LastModifiedString = new Date(p.LastModified).toLocaleDateString();
                        }
                        if(p.LastModifiedOrReviewed) {
                            p.LastModifiedOrReviewedString = new Date(p.LastModifiedOrReviewed).toLocaleDateString();
                        }
                        if(p.LastApproved) {
                            p.LastApprovedString = new Date(p.LastApproved).toLocaleDateString();
                        }
                        if(p.LastReviewed) {
                            p.LastReviewedString = new Date(p.LastReviewed).toLocaleDateString();
                        }
                        if((p.ProcessOwners || []).some((o: any) => o.UserID == userID)) {
                            p.Mine = true;
                        }
                    });
                    // Items the user owns are listed first
                    const sortedProcesses = [
                        ...resp.data.filter((p: any) => { return p.Mine }),
                        ...resp.data.filter((p: any) => { return !p.Mine }),
                    ]
                    this.allProcesses = sortedProcesses;
                    this.processes = sortedProcesses;
                })
                .catch(e => {
                    console.log(e);
                })

            if(this.showSignOff) {
                await axios.get(`${this.$store.state.apiUrl}/api/signoffs/minepublicuser`)
                    .then(resp => {
                        for(const sig of resp.data){
                            const process = this.allProcesses.find((p: any) => {
                                return p.Slug == sig.Slug;
                            });
                            if(process && !process.HasSignOff) {
                                if(new Date(sig.Date).getTime() > new Date(process.LastModified).getTime()) {
                                    process.HasSignOff = true;
                                    process.SignOffDate = new Date(sig.Date).toLocaleDateString();
                                }
                            }
                        }
                    })
                    .catch(e => {
                        console.log(e);
                    })
            }

            this.search();
        },
        search() {
            let tempProcesses = [];
            if(this.selectedType == 'ALL') {
                tempProcesses = this.allProcesses.filter((p: any) => {
                    return p.Type != 'NODE';
                })
            } else {
                tempProcesses = this.allProcesses.filter((p: any) => {
                    return p.Type == this.selectedType;
                })
            }

            if(this.selectedLocation != 'ALL') {
                tempProcesses = tempProcesses.filter((p: any) => {
                    return p.LocationsJSON && p.LocationsJSON.includes(`"${this.selectedLocation}"`);
                })
            }

            if(this.selectedDepartment != 'ALL') {
                tempProcesses = tempProcesses.filter((p: any) => {
                    return p.DepartmentsJSON && p.DepartmentsJSON.includes(`"${this.selectedDepartment}"`);
                })
            }

            if(this.selectedNodeType != 'ALL') {
                tempProcesses = tempProcesses.filter((p: any) => {
                    return p.TypesJSON && p.TypesJSON.includes(`"${this.selectedNodeType}"`);
                })
            }

            const query = {} as any;
            if(this.$store.state.user.isAdmin) {
                query.location = this.selectedLocation;
            }
            query.department = this.selectedDepartment;
            query.nodeType = this.selectedNodeType;

            if(this.searchQ && this.searchQ.length > 3) {
                const searchQLower = this.searchQ.toLowerCase();
                query.search = searchQLower;
                tempProcesses = tempProcesses.filter((p: any) => {
                    return p.Name.toLowerCase().includes(searchQLower)
                })
            }

            this.$router.replace({
                path: this.$route.path,
                query: query
            }).catch((e: any) => {
                console.log(e);
            });

            let valOne = 1;
            let valTwo = -1;
            if(this.sortDirection == 'DESC') {
                valOne = -1;
                valTwo = 1;
            }

            tempProcesses.sort((a: any, b: any) => {
                const aField = a[this.sortBy] ? a[this.sortBy].toLowerCase() : 'ZZZZZZ';
                const bField = b[this.sortBy] ? b[this.sortBy].toLowerCase() : 'ZZZZZZ';
                if(aField > bField) {
                    return valOne;
                } else if(aField < bField) {
                    return valTwo;
                }
                return 0;
            })

            // Items the user owns stay first within the chosen sort
            this.processes = [
                ...tempProcesses.filter((p: any) => { return p.Mine }),
                ...tempProcesses.filter((p: any) => { return !p.Mine }),
            ];
        },
        closeAddProcess() {
            this.searchQ = '';
            this.getProcesses();
            this.showAddProcess = false;
            this.showEditNode = false;
        },
        closeAICreate() {
            this.showAICreate = false;
            this.getProcesses();
        },
        async deleteProcessConfirm() {
            await axios.post(`${this.$store.state.apiUrl}/api/playbook/deletenode`, this.processToDelete)
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })

            await this.getProcesses();

            this.showConfirmDelete = false;
        },
        async deleteProcess(process: any) {
            this.processToDelete = process;
            if(process.Type == 'PROCESS' || process.Type == 'TIMELINE' || process.Type == 'SHEET' || process.Type == 'CHECKLIST') {
                this.showConfirmDeleteOrDraft = true;
            } else if(process.Type == 'NODE') {
                process.showRoutes = false;
                await this.getRoutes(process);
                if(!process.showRoutes) {
                    return;
                }
                if(process.routes && process.routes.length) {
                    process.showRoutes = true;
                    alert('This node is linked to from other nodes. Remove this node from all routes before you can delete.');
                    return;
                } else {
                    this.showConfirmDelete = true;
                }
            } else {
                this.showConfirmDelete = true;
            }
        },
        async editProcess(process: any, cloneNode: boolean) {
            this.cloneNode = cloneNode;
            this.nodeToEdit = JSON.parse(JSON.stringify(process));
            this.showEditNode = true;
        },
        async getPDF(process: any, sharing?: boolean) {
            if(sharing && (process.Type == 'VIDEO' || process.Type == 'LINK')) {
                let query = '?z=t';
                if(!this.shareData.email && !this.shareData.phone) {
                    alert('Enter an email and/or phone to share this with');
                    return;
                }
                if(this.shareData.email) {
                    if(!this.shareData.email.includes('@') || !this.shareData.email.includes('.')) {
                        alert('Make sure to use a valid email');
                        return;
                    }
                    this.shareData.email = this.shareData.email.trim();
                    query += `&email=${encodeURIComponent(this.shareData.email)}`;
                }
                if(this.shareData.phone) {
                    const sharePhone = normalizePhone(this.shareData.phone);
                    if(!sharePhone) {
                        alert(PHONE_ERROR);
                        return;
                    }
                    query += `&phone=${encodeURIComponent(sharePhone)}`;
                }
                let slug = 'sharevideo';
                let type = 'Video';
                if(process.Type == 'LINK') {
                    slug = 'sharelink';
                    type = 'Link';
                }
                this.gettingPDF = true;
                await axios.get(`${this.$store.state.apiUrl}/api/playbook/${slug}/${process.Slug}${query}`)
                    .then(resp => {
                        alert(`${type} has been shared!`);
                    })
                    .catch(e => {
                        console.log(e?.response?.data || e);
                        alert(e?.response?.data || 'There was an error')
                    })

                this.gettingPDF = false;
                this.showSharePDF = false;
                return;
            }

            if(this.gettingPDF) {
                return;
            }

            let pdfSlug = 'processpdf';
            if(process.Type == 'SHEET') {
                pdfSlug = 'sheetpdf';
            } else if(process.Type == 'TIMELINE') {
                pdfSlug = 'timelinepdf';
            } else if(process.Type == 'CHECKLIST') {
                pdfSlug = 'checklistpdf';
            }

            let query = '';
            if(sharing) {
                query = '?z=t&sharing=t';
                if(!this.shareData.email && !this.shareData.phone) {
                    alert('Enter an email and/or phone to share this with');
                    return;
                }
                if(this.shareData.email) {
                    if(!this.shareData.email.includes('@') || !this.shareData.email.includes('.')) {
                        alert('Make sure to use a valid email');
                        return;
                    }
                    this.shareData.email = this.shareData.email.trim();
                    query += `&email=${encodeURIComponent(this.shareData.email)}`;
                }
                if(this.shareData.phone) {
                    const sharePhone = normalizePhone(this.shareData.phone);
                    if(!sharePhone) {
                        alert(PHONE_ERROR);
                        return;
                    }
                    query += `&phone=${encodeURIComponent(sharePhone)}`;
                }
                if(this.shareData.wDescriptions) {
                    query += '&wDescription=true'
                }
            }

            this.gettingPDF = true;

            await axios.get(`${this.$store.state.apiUrl}/api/playbook/${pdfSlug}/${process.Slug}${query}`)
                .then(resp => {
                    if(sharing) {
                        alert('Document has been shared!');
                        this.showSharePDF = false;
                    } else {
                        this.PDFUrl = this.$store.state.apiUrl + '/temppdf/' + resp.data.fileName;
                        this.showPDF = true;
                    }
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert(sharing ? (e?.response?.data || 'There was an error') : 'There was an error')
                })

            this.gettingPDF = false;
        },
        sharePDF(process: any) {
            this.shareProcess = process;
            this.shareData = {
                email: '',
                phone: '',
                wDescriptions: false
            }
            this.showSharePDF = true;
        },
        copyLink() {
            const link = window.location.origin + this.$router.resolve('/playbook/' + this.shareProcess.Slug).href;
            navigator.clipboard.writeText(link);
        },
        changeSort(column: string) {
            if(column == this.sortBy) {
                this.sortDirection = this.sortDirection == 'ASC' ? 'DESC' : 'ASC';
            } else {
                this.sortBy = column;
                this.sortDirection = 'ASC';
            }
            this.search();
        },
        async getRoutes(process: any) {
            if(process.showRoutes) {
                process.showRoutes = false;
            } else {
                await axios.get(`${this.$store.state.apiUrl}/api/playbook/paths?slug=${process.Slug}`)
                    .then(resp => {
                        process.routes = resp.data;
                        process.showRoutes = true;
                    })
                    .catch(e => {
                        alert('There was an error')
                        console.log(e?.response?.data || e);
                    })
            }
        },
        unapproveItem(type: string) {
            axios.post(`${this.$store.state.apiUrl}/api/playbook/node/unapprove/${this.processToDelete.Slug}`, { type: type })
                .then(resp => {
                    this.getProcesses();
                    this.$store.dispatch('setStoreData');
                    alert(this.processToDelete.Name + ' has been moved to ' + type);
                    this.showConfirmDeleteOrDraft = false;
                })
                .catch(e => {
                    console.log(e?.response?.data || e);
                    alert('There was an error')
                })
        },
        showsPDF(process: any): boolean {
            return process.Type == 'PROCESS' || process.Type == 'TIMELINE' || process.Type == 'SHEET' || process.Type == 'CHECKLIST';
        },
        showsShare(process: any): boolean {
            return this.showsPDF(process) || process.Type == 'VIDEO' || process.Type == 'LINK';
        },
        rowMenuAvailable(process: any): boolean {
            return this.canEditNodes || this.showsShare(process);
        },
        toggleRowMenu(process: any) {
            this.openMenuSlug = this.openMenuSlug == process.Slug ? null : process.Slug;
        },
        closeRowMenu() {
            this.openMenuSlug = null;
        },
        rowMenuAction(action: any) {
            this.closeRowMenu();
            action();
        },
        handleOutsideRowMenuClick(e: MouseEvent) {
            if(this.openMenuSlug && !(e.target as HTMLElement).closest('.row-menu')) {
                this.openMenuSlug = null;
            }
        },
        addToFolder(process: any) {
            this.slugToAddToFolder = process.Slug;
            this.showConfirmAddToFolder = true;
        }
    },
    mounted() {
        document.addEventListener('click', this.handleOutsideRowMenuClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideRowMenuClick);
    }
});
</script>
